/**
 * Helper function to terminate pending requests when a user is relieved from an office
 * This function handles Asset Requests, Consumable Requests, and Complaints
 * 
 * @param {string} userId - The ID of the user being relieved
 * @param {string} office - The office from which the user is being relieved
 * @param {Object} hooks - Object containing RTK Query hooks
 * @returns {Promise<Object>} - Results of termination operations
 */

import nameStringFn from "../../data/nameStringFn";

// Helper function to convert name object to name string
const formatNameString = (nameObj) => {
  if (!nameObj) return 'Unknown User';
  
  const { firstName = '', middleName = '', lastName = '' } = nameObj;
  
  // Filter out empty names and join with spaces
  const nameParts = [firstName, middleName, lastName].filter(part => part && part.trim());
  
  return nameParts.join(' ').trim() || 'Unknown User';
};

const terminateUserRequests = async (userId, office, hooks) => {

  
  const results = {
    assetRequests: { terminated: 0, errors: [] },
    consumableRequests: { terminated: 0, errors: [] },
    complaints: { terminated: 0, errors: [] },
    totalTerminated: 0,
    totalErrors: 0
  };

  try {
    // Get current timestamp
    const currentTimestamp = new Date().toISOString();
    
    // Create termination reason message
    const terminationReason = `Request terminated due to user office change. User ${userId} has been relieved from ${office} office.`;

    // ====== TERMINATE ASSET REQUESTS ======
    try {
      console.log(`Terminating asset requests for user ${userId} in office ${office}`);
      
      // Get filtered asset requests for the user
      const assetRequestsResponse = await hooks.getFilteredAssetRequests({
        page: 1,
        limit: 1000, // Get all requests
        filters: {
          perm_req_creator_id: [userId], // Only requests created by this user
          perm_curr_status: ['Pending'] // Only pending requests
        }
      }).unwrap();

      const assetRequests = assetRequestsResponse?.requests || [];

      for (const request of assetRequests) {
        try {
          // Check if request is still pending, created by this user, and from the office
          if (request.curr_status === 'Pending' && 
              request.req_creator_id === userId &&
              request.req_data?.[0]?.sent_by?.office === office) {

            // Get the current holder from the last req_data entry
            const lastReqData = request.req_data?.[request.req_data.length - 1];
            const currentHolder = lastReqData?.sent_to || {};

            // Create termination flow entry
            const terminationFlowEntry = {
              sent_to_tag: 'req_term_off_chng',
              Text: `Request terminated due to user office change. User ${formatNameString(request.req_data[0]?.sent_by.name)} has been relieved from ${office} office.`,
              sent_by: {
                id: currentHolder?.id ,
                name: currentHolder?.name ,
                office: currentHolder?.office ,
                designation: currentHolder?.designation ,
              },
              sent_to: {
                id: request.req_creator_id, // Request creator
                name: request.req_data[0]?.sent_by.name ,
                office: request.req_data[0]?.sent_by?.office,
                designation: request.req_data[0]?.sent_by?.designation,
              },
              action: 'Request Terminated - Office Change',
              time_stamp: currentTimestamp,
              status: 'Terminated'
            };

            // Update the asset request
            const updatePayload = {
              id: request._id || request.id,
              senttoData: {
                ...terminationFlowEntry,
                curr_status: 'Terminated',
                curr_sent_to_string: `${formatNameString(request.req_data[0]?.sent_by.name)}-${request.req_data[0]?.sent_by?.office}-${request.req_data[0]?.sent_by?.designation}`,
                sent_by: terminationFlowEntry.sent_by,
                sent_to: terminationFlowEntry.sent_to
              }
            };
            await hooks.updateAssetReq(updatePayload).unwrap();

            results.assetRequests.terminated++;
            console.log(`Terminated asset request ${request._id || request.id}`);
          }
        } catch (error) {
          console.error(`Error terminating asset request ${request._id || request.id}:`, error);
          results.assetRequests.errors.push({
            requestId: request._id || request.id,
            error: error.message
          });
        }
      }
    } catch (error) {
      console.error('Error fetching asset requests:', error);
      results.assetRequests.errors.push({
        operation: 'fetch',
        error: error.message
      });
    }

    // ====== TERMINATE CONSUMABLE REQUESTS ======
    try {
      console.log(`Terminating consumable requests for user ${userId} in office ${office}`);
      
      // Get filtered consumable requests for the user
      const consumableRequestsResponse = await hooks.getFilteredConsumableRequests({
        page: 1,
        limit: 1000, // Get all requests
        filters: {
          perm_req_creator_id: [userId], // Only requests created by this user
          perm_curr_status: ['Pending'] // Only pending requests
        }
      }).unwrap();

      const consumableRequests = consumableRequestsResponse?.requests || [];

      for (const request of consumableRequests) {
        try {
          // Check if request is still pending, created by this user, and from the office
          if (request.curr_status === 'Pending' && 
              request.req_creator_id === userId &&
              request.req_data?.[0]?.sent_by?.office === office) {

            // Get the current holder from the last req_data entry
            const lastReqData = request.req_data?.[request.req_data.length - 1];
            const currentHolder = lastReqData?.sent_to || {};

            // Create termination flow entry
            const terminationFlowEntry = {
              sent_to_tag: 'consumreq_term_off_chng',
              Text: terminationReason,
              sent_by: {
                id: currentHolder?.id,
                name: currentHolder?.name,
                office: currentHolder?.office,
                designation: currentHolder?.designation,
              },
              sent_to: {
                id: request.req_creator_id, // Request creator
                name: request.req_data[0]?.sent_by.name,
                office: request.req_data[0]?.sent_by?.office,
                designation: request.req_data[0]?.sent_by?.designation,
              },
              action: 'Consumable Request Terminated - Office Change',
              time_stamp: currentTimestamp,
              status: 'Terminated'
            };

            // Update the consumable request
            const updatePayload = {
              id: request._id || request.id,
              senttoData: {
                ...terminationFlowEntry,
                curr_status: 'Terminated',
                curr_sent_to_string: `${formatNameString(request.req_data[0]?.sent_by.name)}-${request.req_data[0]?.sent_by?.office}-${request.req_data[0]?.sent_by?.designation}`,
                sent_by: terminationFlowEntry.sent_by,
                sent_to: terminationFlowEntry.sent_to
              }
            };

            await hooks.updateConsumableReq(updatePayload).unwrap();

            results.consumableRequests.terminated++;
            console.log(`Terminated consumable request ${request._id || request.id}`);
          }
        } catch (error) {
          console.error(`Error terminating consumable request ${request._id || request.id}:`, error);
          results.consumableRequests.errors.push({
            requestId: request._id || request.id,
            error: error.message
          });
        }
      }
    } catch (error) {
      console.error('Error fetching consumable requests:', error);
      results.consumableRequests.errors.push({
        operation: 'fetch',
        error: error.message
      });
    }

    // ====== TERMINATE COMPLAINTS ======
    try {
      console.log(`Terminating complaints for user ${userId} in office ${office}`);
      
      // Get filtered complaints for the user
      const complaintsResponse = await hooks.getFilteredComplaints({
        page: 1,
        limit: 1000, // Get all complaints
        filters: {
          perm_req_creator_id: [userId], // Only complaints created by this user
          perm_curr_status: ['Open'] // Only open complaints (complaints use 'Open' instead of 'Pending')
        }
      }).unwrap();

      const complaints = complaintsResponse?.complaints || [];

      for (const complaint of complaints) {
        try {
          // Check if complaint is still open, created by this user, and from the office
          if (complaint.curr_status === 'Open' && 
              complaint.req_creator_id === userId &&
              complaint.req_data?.[0]?.sent_by?.office === office) {

            // Get the current holder from the last req_data entry
            const lastReqData = complaint.req_data?.[complaint.req_data.length - 1];
            const currentHolder = lastReqData?.sent_to || {};

            // Create termination flow entry for complaints
            const terminationFlowEntry = {
              action: 'Complaint Terminated - Office Change',
              sent_to_tag: 'compl_term_off_chng',
              text: terminationReason,
              sent_by: {
                id: currentHolder?.id,
                name: currentHolder?.name,
                office: currentHolder?.office,
                designation: currentHolder?.designation
              },
              sent_to: {
                id: complaint.req_creator_id, // Complaint creator
                name: complaint.req_data[0]?.sent_by.name,
                office: complaint.req_data[0]?.sent_by?.office,
                designation: complaint.req_data[0]?.sent_by?.designation
              },
              time_stamp: currentTimestamp
            };

            // Update the complaint
            const updatePayload = {
              id: complaint._id || complaint.id,
              req_data_entry: terminationFlowEntry,
              curr_status: 'Terminated',
              curr_sent_by: currentHolder?.id,
              curr_sent_to: complaint.req_creator_id || userId,
              curr_sent_to_string: `${formatNameString(complaint.req_data[0]?.sent_by.name)}-${complaint.req_data[0]?.sent_by?.office}-${complaint.req_data[0]?.sent_by?.designation}`,
              curr_sent_to_tag: 'compl_term_off_chng',
              opened: false
            };

            await hooks.updateComplaints(updatePayload).unwrap();

            results.complaints.terminated++;
            console.log(`Terminated complaint ${complaint._id || complaint.id}`);
          }
        } catch (error) {
          console.error(`Error terminating complaint ${complaint._id || complaint.id}:`, error);
          results.complaints.errors.push({
            complaintId: complaint._id || complaint.id,
            error: error.message
          });
        }
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
      results.complaints.errors.push({
        operation: 'fetch',
        error: error.message
      });
    }

    // Calculate totals
    results.totalTerminated = 
      results.assetRequests.terminated + 
      results.consumableRequests.terminated + 
      results.complaints.terminated;

    results.totalErrors = 
      results.assetRequests.errors.length + 
      results.consumableRequests.errors.length + 
      results.complaints.errors.length;

    console.log('Request termination completed:', results);
    return results;

  } catch (error) {
    console.error('Critical error in terminateUserRequests:', error);
    throw new Error(`Failed to terminate user requests: ${error.message}`);
  }
};

export default terminateUserRequests;
